import { CanvasPool } from '../render/utils/canvas-pool';

export class CanvasProvider {
  constructor(options = {}) {
    this.stageContext = null;
    this.activeCanvases = new Map(); // 追踪活跃的canvas
    this._maxCanvasWarningEmitted = false; // 添加标志位防止重复警告
    this.options = {
      enablePooling: options.enablePooling !== false, // 默认启用池化
      maxActiveCanvases: options.maxActiveCanvases || 200,
      autoOptimize: options.autoOptimize !== false,
      ...options,
    };
    this.canvasPool = new CanvasPool({ maxPoolSize: this.options.maxActiveCanvases });

    // 启动自动优化
    if (this.options.autoOptimize) {
      this.canvasPool.startAutoOptimization(5000);
    }
  }

  attach(stageContext) {
    this.stageContext = stageContext;

    // 将canvas相关方法绑定到渲染上下文
    if (stageContext) {
      stageContext.canvasProvider = this;

      // 提供便捷的canvas操作方法
      stageContext.createCanvas = (width, height, id) => this.createCanvas(width, height, id);
      stageContext.releaseCanvas = (canvas) => this.releaseCanvas(canvas);
      stageContext.getCanvasStats = () => this.getStats();
    }
  }

  dispose() {
    // 清理所有活跃的canvas
    this.activeCanvases.forEach((canvas) => {
      this.releaseCanvas(canvas);
    });
    this.activeCanvases.clear();

    // 停止自动优化
    if (this.canvasPool) {
      this.canvasPool.stopAutoOptimization();
    }

    // 重置警告标志位
    this._maxCanvasWarningEmitted = false;
    this.stageContext = null;
  }

  // 创建canvas
  createCanvas(width, height, id = null) {
    if (!this.options.enablePooling) {
      return this._createDirectCanvas(width, height);
    }

    // 检查活跃canvas数量限制
    if (this.activeCanvases.size >= this.options.maxActiveCanvases) {
      // 只在首次达到限制时发出警告
      if (!this._maxCanvasWarningEmitted) {
        console.warn('Canvas pool: Maximum active canvases reached, creating direct canvas');
        this._maxCanvasWarningEmitted = true;
      }
      return this._createDirectCanvas(width, height);
    }

    const poolItem = this.canvasPool.acquire(width, height);
    const { canvas, context } = poolItem;

    // 为canvas添加唯一标识和释放方法
    const canvasId = id || this._generateCanvasId();
    canvas._poolId = canvasId;
    canvas._poolItem = poolItem;
    canvas._isPooled = true;

    // 添加便捷的释放方法
    canvas.release = () => this.releaseCanvas(canvas);

    // 追踪活跃的canvas
    this.activeCanvases.set(canvasId, canvas);

    return { canvas, context, id: canvasId };
  }

  // 直接创建canvas（不使用池）
  _createDirectCanvas(width, height) {
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');
    canvas.width = width;
    canvas.height = height;
    canvas._isPooled = false;
    canvas.release = () => {}; // 空函数，保持API一致性

    return { canvas, context, id: this._generateCanvasId() };
  }

  // 释放canvas
  releaseCanvas(canvas) {
    if (!canvas) return;

    const canvasId = canvas._poolId;
    if (canvasId && this.activeCanvases.has(canvasId)) {
      this.activeCanvases.delete(canvasId);
    }

    // 如果释放canvas后数量降到限制以下，重置警告标志位
    if (this.activeCanvases.size < this.options.maxActiveCanvases) {
      this._maxCanvasWarningEmitted = false;
    }

    if (canvas._isPooled && canvas._poolItem) {
      this.canvasPool.release(canvas._poolItem);
      // 清理引用
      delete canvas._poolId;
      delete canvas._poolItem;
      delete canvas._isPooled;
      delete canvas.release;
    }
  }

  // 获取池化统计信息
  getStats() {
    const poolStats = this.canvasPool.getStats();
    return {
      ...poolStats,
      activeCanvases: this.activeCanvases.size,
      maxActiveCanvases: this.options.maxActiveCanvases,
      poolingEnabled: this.options.enablePooling,
    };
  }

  // 清理池
  clearPool() {
    // 先释放所有活跃的canvas
    this.activeCanvases.forEach((canvas) => {
      this.releaseCanvas(canvas);
    });
    this.activeCanvases.clear();

    // 清理池
    this.canvasPool.clear();
  }

  // 优化池大小
  optimizePool() {
    this.canvasPool.optimizePoolSize();
  }

  // 创建离屏canvas用于渲染
  createOffscreenCanvas(width, height, dpr = 1) {
    // 目标 DPR，至少为 1
    let targetDpr = Math.max(1, dpr);

    // 计算初始像素尺寸
    let actualWidth = Math.ceil(width * targetDpr);
    let actualHeight = Math.ceil(height * targetDpr);

    // 安全尺寸上限（大多数浏览器的安全值）。允许通过 options 覆盖
    const maxDim = this.options.maxOffscreenDimension || 16384;

    // 若超出最大尺寸，按比例降低 DPR，避免创建失败导致渲染空白
    const scaleToFit = Math.min(
      maxDim / Math.max(1, actualWidth),
      maxDim / Math.max(1, actualHeight),
      1
    );
    if (scaleToFit < 1) {
      targetDpr = targetDpr * scaleToFit;
      actualWidth = Math.ceil(width * targetDpr);
      actualHeight = Math.ceil(height * targetDpr);
      if (!this._maxCanvasWarningEmitted) {
        console.warn(
          `CanvasProvider: downscaling offscreen DPR to ${targetDpr.toFixed(2)} to fit max ${maxDim}px`
        );
        this._maxCanvasWarningEmitted = true;
      }
    }

    const canvasInfo = this.createCanvas(actualWidth, actualHeight);
    const { canvas, context } = canvasInfo;

    // 设置样式尺寸
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';

    // 设置缩放（使用降级后的 DPR）
    context.scale(targetDpr, targetDpr);

    return canvasInfo;
  }

  // 创建用于缓存的canvas
  createCacheCanvas(node, scale = 1) {
    const width = Math.ceil(Math.abs(node.w * scale));
    const height = Math.ceil(Math.abs(node.h * scale));

    return this.createCanvas(width, height, `cache_${node.id}_${scale}`);
  }

  // 批量释放canvas
  releaseBatch(canvases) {
    if (!Array.isArray(canvases)) return;

    canvases.forEach((canvas) => {
      this.releaseCanvas(canvas);
    });
  }

  // 生成canvas ID
  _generateCanvasId() {
    return `canvas_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 内存压力下的紧急清理
  emergencyCleanup() {
    // 释放一半的活跃canvas
    const canvasesToRelease = Array.from(this.activeCanvases.values()).slice(
      0,
      Math.floor(this.activeCanvases.size / 2)
    );

    canvasesToRelease.forEach((canvas) => {
      this.releaseCanvas(canvas);
    });

    // 清理池中的旧canvas
    this.canvasPool.clear();
  }

  // 预热canvas池
  warmUpPool(sizes = []) {
    const defaultSizes = [
      { width: 100, height: 100 },
      { width: 200, height: 200 },
      { width: 500, height: 500 },
      { width: 1000, height: 1000 },
    ];

    const sizesToWarmUp = sizes.length > 0 ? sizes : defaultSizes;

    sizesToWarmUp.forEach((size) => {
      const canvasInfo = this.createCanvas(size.width, size.height);
      // 立即释放，这样canvas会进入池中
      setTimeout(() => {
        this.releaseCanvas(canvasInfo.canvas);
      }, 0);
    });
  }
}
